import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTheme } from '../../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import SlideInPanel from '../common/SlideInPanel';
import { workService } from '../../services/workService';
import { WorkStatusBadge } from './WorkStatusBadge';
import { PriorityChip } from './PriorityChip';
import { ProgressBar } from './ProgressBar';
import { DueDateLabel } from './DueDateLabel';
import UserBadge from '../common/UserBadge';
import { FiExternalLink } from 'react-icons/fi';

interface Props {
  goalId: string;
  onClose: () => void;
}

export const GoalDetailPanel: React.FC<Props> = ({ goalId, onClose }) => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const { data: goal } = useQuery(['goal', goalId], () => workService.getGoal(goalId));
  const { data: projects = [] } = useQuery(['projects', goalId], () => workService.getProjects({ goal_id: goalId }));

  if (!goal) return null;

  return (
    <SlideInPanel open title={goal.title} subtitle={goal.description || undefined} accentColor="emerald" onClose={onClose}>
      {/* Actions */}
      <div className="flex gap-2 mb-4">
        <button onClick={() => { onClose(); navigate(`/goals/${goalId}`); }}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-sm ${darkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
          <FiExternalLink className="w-3.5 h-3.5" /> Open Full Page
        </button>
      </div>

      {/* Meta */}
      <div className="flex items-center gap-3 mb-4">
        <WorkStatusBadge status={goal.status} size="md" />
        <PriorityChip priority={goal.priority} />
        <DueDateLabel date={goal.due_date} />
        <UserBadge owner={goal.owner} size="sm" />
      </div>

      <ProgressBar pct={0} accent="emerald" shimmer={goal.status === 'completed'} />

      {/* Projects */}
      <h3 className={`text-sm font-semibold mt-6 mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
        Projects ({projects.length})
      </h3>
      <div className="space-y-2">
        {projects.map(p => (
          <div key={p.id}
            onClick={() => { onClose(); navigate(`/projects/${p.id}`); }}
            className={`p-3 rounded-lg border cursor-pointer transition-colors ${darkMode ? 'bg-gray-800 border-gray-700 hover:bg-gray-700/80 border-l-2 border-l-teal-500' : 'bg-white border-gray-200 hover:bg-gray-50 border-l-2 border-l-teal-500'}`}
          >
            <div className="flex items-center justify-between">
              <span className={`font-medium text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>{p.title}</span>
              <WorkStatusBadge status={p.status} />
            </div>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs text-gray-500">{p.task_count ?? 0} tasks</span>
              <DueDateLabel date={p.due_date} />
            </div>
          </div>
        ))}
        {projects.length === 0 && <p className="text-sm text-gray-500">No projects under this goal</p>}
      </div>
    </SlideInPanel>
  );
};
